import React, { useState, createContext } from "react";
import { EUROPE_DOMAINNAME, NODE_LIST_KEY, USER_NAME_KEY, BUSINESS_SPACE_KEY, parseBusinessSpace } from "../constants";
import { BusinessSpace } from "../admin-api/generated-src/api";

interface adminStateProps {
  username: string;
  selectedNode: string;
  selectedBusinessSpace: BusinessSpace | "";
  nodeList: string[];
  isSideBarOpen: boolean;
  isEUDomain: boolean;
  changeSelectedNode(nodeId: string): any;
  changeSelectedBusinessSpace(businessSpace: BusinessSpace | ""): any;
  toggleSideBar(): any;
  loadNodeList(nodeList: string[]): any;
  changeUsername(username: string): any;
}

export const initialState: adminStateProps = {
  username: "",
  selectedNode: "",
  selectedBusinessSpace: "",
  nodeList: [],
  isSideBarOpen: true,
  isEUDomain: false,
  changeSelectedNode: () => { },
  changeSelectedBusinessSpace: () => { },
  toggleSideBar: () => { },
  loadNodeList: () => { },
  changeUsername: () => { },
};

const AdminContext = createContext(initialState);

/**
 * Safely parse the persisted node list. A stale/legacy value written by an older build may not
 * be valid JSON (or not an array); parsing it directly in the useState initializer would throw
 * and crash the whole app at boot (white screen). On any failure, drop the corrupt key so it
 * self-heals on the next fetch, and fall back to the empty default.
 */
export function parseStoredNodeList(stored: string | null): string[] {
  if (!stored) {
    return initialState.nodeList;
  }
  try {
    const parsed = JSON.parse(stored);
    return Array.isArray(parsed) ? parsed : initialState.nodeList;
  } catch {
    localStorage.removeItem(NODE_LIST_KEY);
    return initialState.nodeList;
  }
}

export function AdminContextProvider({ children }) {
  const [sessionUsername, setSessionUsername] = useState(localStorage.getItem(USER_NAME_KEY) || initialState.username);
  const [userSelectedNode, setUserSelectedNode] = useState(
    initialState.selectedNode
  );
  const [userSelectedBusinessSpace, setUserSelectedBusinessSpace] = useState<BusinessSpace | "">(
    parseBusinessSpace(localStorage.getItem(BUSINESS_SPACE_KEY))
  );
  const [userIsSideBarOpen, setUserIsSideBarOpen] = useState(
    initialState.isSideBarOpen
  );
  const localStorageNodeList = localStorage.getItem(NODE_LIST_KEY);
  const [userNodeList, setUserNodeList] = useState(parseStoredNodeList(localStorageNodeList));

  function changeSelectedNode(nodeId) {
    setUserSelectedNode(nodeId);
  }
  function changeSelectedBusinessSpace(businessSpace: BusinessSpace | "") {
    localStorage.setItem(BUSINESS_SPACE_KEY, businessSpace);
    setUserSelectedBusinessSpace(businessSpace);
  }
  function loadNodeList(nodeList) {
    setUserNodeList(nodeList);
  }

  function toggleSideBar() {
    setUserIsSideBarOpen((prevIsBarOpen) => !prevIsBarOpen);
  }

  function changeUsername(username) {
    setSessionUsername(username);
  }

  const context: adminStateProps = {
    username: sessionUsername,
    selectedNode: userSelectedNode,
    selectedBusinessSpace: userSelectedBusinessSpace,
    nodeList: userNodeList,
    isSideBarOpen: userIsSideBarOpen,
    isEUDomain: (window.location.hostname).includes(EUROPE_DOMAINNAME),
    changeSelectedNode,
    changeSelectedBusinessSpace,
    loadNodeList,
    toggleSideBar,
    changeUsername,
  };

  return (
    <AdminContext.Provider value={context}>{children}</AdminContext.Provider>
  );
}

export default AdminContext;
